<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Splash Page
                </h2>
                <p>Introduce the site to visitors. When enabled, logged-out visitors see this page instead of the program list on the home page. Link to <strong>/programs</strong> to send them to the programs.</p>
            </div>
        </div>

        <div class="bg-white shadow rounded-lg p-6">
            <div class="mb-4">
                <label class="flex items-center gap-3 cursor-pointer mb-4">
                    <d-switch v-model="splashPageEnabled" />
                    <span class="font-semibold">{{ splashPageEnabled ? 'Splash Page Enabled' : 'Splash Page Disabled' }}</span>
                </label>
            </div>

            <!-- The editor is only offered once the splash page is turned on. Turning it off
                 keeps the saved content, so it comes back if the splash page is re-enabled. -->
            <d-field v-if="splashPageEnabled" label="Splash Page Content">
                <div class="splash-content-editor">
                    <l-html-editor v-model="splashPageHtml" />
                </div>
            </d-field>

            <div class="mt-4 flex gap-3">
                <d-btn
                    color="primary"
                    :loading="saving"
                    @click="saveSplashPageSettings"
                >
                    Save Splash Page
                </d-btn>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { toast } from '@services/toast.service';

export default {
    name: 'SplashPage',

    data() {
        return {
            splashPageEnabled: false,
            splashPageHtml: '',
            saving: false,
        };
    },

    mounted() {
        this.loadSplashPageSettings();
    },

    methods: {
        async loadSplashPageSettings() {
            try {
                const { data } = await axios.get('/api/admin/splash-page');
                this.splashPageEnabled = data.splashPageEnabled;
                this.splashPageHtml = data.splashPageHtml || '';
            } catch (error) {
                console.error('Error loading splash page settings:', error);
                toast.error('Failed to load splash page settings.');
            }
        },

        async saveSplashPageSettings() {
            this.saving = true;
            try {
                await axios.put('/api/admin/splash-page', {
                    splashPageEnabled: this.splashPageEnabled,
                    splashPageHtml: this.splashPageHtml,
                });
                toast.success('Splash page saved successfully.');
            } catch (error) {
                console.error('Error saving splash page settings:', error);
                toast.error('Failed to save splash page settings.');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<!--
    Preview styles: make the editor look like the public splash page ("Option B" brand look), so
    authors see cards and buttons while writing instead of plain bullet lines. Mapping:
      bullet list                -> row of cards (green edge)
      numbered list              -> navy cards, yellow edge (no automatic numbers: authors type their own)
      centered bullet list       -> mint highlight cards
      bold text in a list item   -> card title
      Size "Small"               -> grey label / fine print
      link with a background     -> filled button (brand color, whatever color was picked)
      plain link                 -> outline button
      last paragraph with links  -> navy call-to-action band
    Keep in sync with the .splash-content rules in Content.Web/Resources/styles/main.css.
    Admin's theme has no --color-accent / --color-primary-100, hence the fallbacks.
    :deep() reaches into the editor component and the scoped attribute keeps these rules
    from affecting any other editor in the admin.
-->
<style scoped>
.splash-content-editor :deep(.ql-editor) {
    color: var(--color-primary-500, #12263a);
    background: linear-gradient(180deg, var(--color-primary-100, #f3f7fb) 0, #ffffff 420px);
    padding: 24px;
}

.splash-content-editor :deep(.ql-editor h1) {
    font-size: 2.25rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--color-primary-500, #12263a);
    margin-bottom: 0.75rem;
}

.splash-content-editor :deep(.ql-editor h2) {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--color-primary-500, #12263a);
    margin: 2.25rem 0 1rem;
}

.splash-content-editor :deep(.ql-editor p) {
    margin: 0.6rem 0;
    font-size: 1.05rem;
}

.splash-content-editor :deep(.ql-editor .ql-size-small) {
    font-size: 0.8rem;
    color: #6b7280;
}

.splash-content-editor :deep(.ql-editor a) {
    display: inline-block;
    padding: 0.5rem 1rem;
    margin: 0.35rem 0.25rem;
    border: 1px solid var(--color-primary-500, #12263a);
    border-radius: 0.4rem;
    color: var(--color-primary-500, #12263a);
    background: #fff;
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
}

.splash-content-editor :deep(.ql-editor a[style*="background"]) {
    background: var(--color-accent, #01D28E) !important;
    color: var(--color-primary-500, #12263a) !important;
    border-color: transparent;
    font-weight: 700;
}

.splash-content-editor :deep(.ql-editor ul),
.splash-content-editor :deep(.ql-editor ol) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 0;
    margin: 0;
}

.splash-content-editor :deep(.ql-editor li) {
    border: 1px solid #e3ebf4;
    border-left: 5px solid var(--color-accent, #01D28E);
    border-radius: 0.6rem;
    padding: 1rem 1.1rem;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    font-size: 0.92rem;
    color: #374151;
}

/* Quill draws bullets and numbers with ::before; cards don't show either (the public page has none) */
.splash-content-editor :deep(.ql-editor li::before) {
    content: none;
}

.splash-content-editor :deep(.ql-editor li .ql-size-small) {
    display: block;
    margin-bottom: 0.2rem;
}

.splash-content-editor :deep(.ql-editor li strong) {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-primary-500, #12263a);
    margin-bottom: 0.45rem;
}

.splash-content-editor :deep(.ql-editor li a) {
    display: table;
    margin: 0.8rem 0 0;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
}

.splash-content-editor :deep(.ql-editor li a[style*="background"]) {
    background: var(--color-primary-500, #12263a) !important;
    color: #fff !important;
}

/* Numbered list: navy cards with a yellow edge, no automatic numbers */
.splash-content-editor :deep(.ql-editor ol > li) {
    background: var(--color-primary-500, #12263a);
    border-color: var(--color-primary-500, #12263a);
    border-left-color: var(--color-secondary-500, #f5b800);
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
}

.splash-content-editor :deep(.ql-editor ol > li strong) {
    color: #fff;
}

.splash-content-editor :deep(.ql-editor ol > li .ql-size-small) {
    color: #cbd5e1;
}

/* A navy card button would vanish on the navy card: filled buttons here are green */
.splash-content-editor :deep(.ql-editor ol > li a[style*="background"]) {
    background: var(--color-accent, #01D28E) !important;
    color: var(--color-primary-500, #12263a) !important;
}

/* Centered bullet list: mint highlight cards */
.splash-content-editor :deep(.ql-editor li.ql-align-center) {
    background: #e9fbf4;
    border: 1px solid #b8f0dc;
    border-top: 4px solid var(--color-accent, #01D28E);
    color: var(--color-primary-500, #12263a);
    font-size: 1rem;
    font-weight: 600;
}

/* Closing call to action: last paragraph with buttons sits in a navy band */
.splash-content-editor :deep(.ql-editor > p:last-child:has(a)) {
    background: var(--color-primary-500, #12263a);
    border-radius: 0.6rem;
    padding: 1.25rem;
    margin-top: 0.75rem;
}

.splash-content-editor :deep(.ql-editor > p:last-child:has(a) a:not([style*="background"])) {
    background: transparent;
    color: #fff;
    border-color: #fff;
}
</style>
